import { RequestDetailsVM, RequestStatusEnum, requestApis } from "@payping/api";
import { DashboardRoutes } from "@/router/constant";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import {
  Badge,
  Button,
  CardList,
  Copy,
  DataHeader,
  Flex,
  Grid,
  Space,
  Tag,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import React from "react";
import { Link, generatePath, useNavigate } from "react-router-dom";

import { useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import qs from "query-string";
import { cardDeleteId, lastRequestsViewAllId } from "@/assets/googleTrackIds";
import { DisplayModeEnum, ListCardProps } from "@payping/web-components/components/List/types";
import { FormOutlined, PhRCheckCircle, PhRWarningOctagon } from "@payping/icons";
import { CreateStepEnum } from "@/pages/Settlement/Create";

interface Props {
  retryList: () => void;
}

const LatelyRequestList: React.FC<Props> = ({ retryList }) => {
  const { t } = useAppTranslation();
  const { requestList } = useAppSelector((state) => state.cashOuts);
  const navigate = useNavigate();
  const { md } = Grid.useBreakpoint();

  const TagStatus = ({ status }: { status?: RequestStatusEnum }) => {
    switch (status) {
      case RequestStatusEnum.Unknown:
        return (
          <Tag className={"!tw-m-0"} color={"orange"}>
            {t(`settlement.request.status.Unknown`)}
          </Tag>
        );
      case RequestStatusEnum.Final:
        return <Tag className={"!tw-m-0"}>{t(`settlement.request.status.Final`)}</Tag>;
      case RequestStatusEnum.Failed:
        return (
          <Tag className={"!tw-m-0"} color={"red"}>
            {t(`settlement.request.status.Failed`)}
          </Tag>
        );
      case RequestStatusEnum.Draft:
        return <Tag color={"default"}>{t(`settlement.request.status.Draft`)}</Tag>;
      default:
        return undefined;
    }
  };

  const IconStatus = ({ status }: { status?: RequestStatusEnum }) => {
    switch (status) {
      case RequestStatusEnum.Final:
        return <PhRCheckCircle color={"green"} width={24} />;
      case RequestStatusEnum.Failed:
        return <PhRWarningOctagon color={"red"} width={24} />;
      case RequestStatusEnum.Draft:
        return <FormOutlined color={"grey"} width={14} />;
      default:
        return undefined;
    }
  };

  const mobileDescriptionContent = (settlement: RequestDetailsVM) => (
    <>
      <Copy text={settlement?.code || ""}>
        <Typography.Text type={"secondary"}>{settlement.code}</Typography.Text>
      </Copy>

      {/* < NumberOfPayments > */}
      <Flex justify={"space-between"}>
        <Typography.Text type={"secondary"}>{t("settlement.numberOfPayments")}</Typography.Text>
        <Typography.Text>
          {(settlement?.succeededCount || 0) + (settlement?.failedCount || 0) + (settlement?.inProgressCount || 0)}
        </Typography.Text>
      </Flex>

      {/* < PaymentStatus > */}
      <Flex gap={12} justify={"space-between"}>
        {/* TITLE */}
        <Typography.Text className="!tw-m-0 tw-flex-none" type={"secondary"}>
          {t("settlement.request.paymentStatus")}
        </Typography.Text>

        <Flex gap={2} align="baseline" className={"tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap"}>
          {/* ICON AND TEXT */}
          {settlement.status === RequestStatusEnum.Final ? (
            <>
              <Badge dot={true} status={"success"} />
              {settlement?.succeededCount || 0}
              <Typography.Paragraph className="!tw-m-0 !tw-text-center" ellipsis>
                {t("settlement.reportDetail.status.success")}
              </Typography.Paragraph>
            </>
          ) : null}
          {settlement.status === RequestStatusEnum.Unknown ? (
            <>
              {settlement.succeededCount && settlement.succeededCount > 0 ? (
                <Typography.Text type={"secondary"}>{"/"}</Typography.Text>
              ) : null}
              <Badge dot={true} status={"warning"} />
              {settlement?.inProgressCount || 0}
              <Typography.Paragraph className="!tw-m-0 !tw-text-center" ellipsis>
                {t("settlement.reportDetail.status.inProgress")}
              </Typography.Paragraph>
            </>
          ) : null}

          {settlement.status === RequestStatusEnum.Failed ? (
            <>
              {(settlement?.inProgressCount && settlement.inProgressCount > 0) ||
                (settlement?.succeededCount && settlement.succeededCount > 0 ? (
                  <Typography.Text type={"secondary"}>{"/"}</Typography.Text>
                ) : null)}
              <Badge dot={true} status={"error"} />
              {settlement?.failedCount || 0}
              <Typography.Paragraph className="!tw-m-0 !tw-text-center" ellipsis>
                {t("settlement.reportDetail.status.error")}
              </Typography.Paragraph>
            </>
          ) : null}
        </Flex>
      </Flex>
      {/* < PaymentStatus only show for mobiles > */}
      {settlement.status === RequestStatusEnum.Final ? (
        <Flex key={1} justify={"space-between"}>
          <Typography.Text type={"secondary"}>{t("settlement.totalSettled")}</Typography.Text>
          <Typography.Text
            strong
          >{` ${formatLocalizedNumber({ value: settlement.totalAmount ?? 0 })} تومان `}</Typography.Text>
        </Flex>
      ) : null}
    </>
  );
  const desktopDescriptionContent = (settlement: RequestDetailsVM) => (
    <Flex gap={"16px"}>
      <Flex gap={"8px"}>
        <Typography.Text type={"secondary"}>{t("settlement.numberOfPayments")}</Typography.Text>
        <Typography.Text>
          {(settlement?.succeededCount || 0) + (settlement?.failedCount || 0) + (settlement?.inProgressCount || 0)}
        </Typography.Text>
      </Flex>

      <Flex gap={"12px"}>
        <Typography.Text type={"secondary"}>{t("settlement.request.paymentStatus")}</Typography.Text>
        {settlement.status === RequestStatusEnum.Final ? (
          <Flex gap={"12px"}>
            <Badge
              dot={true}
              status={"success"}
              text={`${settlement?.succeededCount || 0} ${t("settlement.reportDetail.status.success")}`}
            />
          </Flex>
        ) : null}
        {settlement.status === RequestStatusEnum.Unknown ? (
          <Flex gap={"12px"}>
            {settlement.succeededCount && settlement.succeededCount > 0 ? (
              <Typography.Text type={"secondary"}>{"/"}</Typography.Text>
            ) : null}
            <Badge
              dot={true}
              status={"warning"}
              text={`${settlement?.inProgressCount || 0} ${t("settlement.reportDetail.status.inProgress")}`}
            />
          </Flex>
        ) : null}
        {settlement.status === RequestStatusEnum.Failed ? (
          <Flex gap={"12px"}>
            {(settlement?.inProgressCount && settlement.inProgressCount > 0) ||
              (settlement?.succeededCount && settlement.succeededCount > 0 ? (
                <Typography.Text type={"secondary"}>{"/"}</Typography.Text>
              ) : null)}
            <Badge
              dot={true}
              status={"error"}
              text={`${settlement?.failedCount || 0} ${t("settlement.reportDetail.status.error")}`}
            />
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  );

  const [{ loading }, onDeleteDraft] = useMessagedAsyncFn(
    async (code: string) => {
      await requestApis.changeRequestStatusWithViewModelWithCancellationTokenPut({
        code,
        toStatus: RequestStatusEnum.Incomplete,
      });
      retryList();
    },
    [retryList],
  );

  const data: ListCardProps["dataSource"] =
    requestList?.data?.map((settlement) => ({
      actions:
        // eslint-disable-next-line no-nested-ternary
        settlement.status === RequestStatusEnum.Draft
          ? [
              <Typography.Link
                onClick={() => onDeleteDraft(settlement.code as string)}
                key={1}
                data-google-track={cardDeleteId}
              >
                {t("common.delete")}
              </Typography.Link>,
              <Typography.Link
                onClick={() =>
                  navigate(
                    generatePath(DashboardRoutes.settlement.create.url, {
                      code: settlement.code as string,
                      step: String(CreateStepEnum.IBANList),
                    }),
                  )
                }
                key={2}
              >
                {t("continue")}
              </Typography.Link>,
            ]
          : md
            ? [
                settlement.status === RequestStatusEnum.Final ? (
                  <Flex key={1} gap={8} wrap>
                    <Typography.Text type={"secondary"}>{t("settlement.totalSettled")}</Typography.Text>
                    <Typography.Text
                      strong
                    >{` ${formatLocalizedNumber({ value: settlement.totalAmount ?? 0 })} تومان `}</Typography.Text>
                  </Flex>
                ) : null,
              ]
            : undefined,

      descriptions:
        settlement.status === RequestStatusEnum.Draft ? undefined : (
          <Space key={1} direction={"vertical"} size={"middle"} className={"tw-w-full"}>
            {!md ? mobileDescriptionContent(settlement) : desktopDescriptionContent(settlement)}
          </Space>
        ),
      image: md
        ? {
            type: "icon",
            content: [<IconStatus key={1} status={settlement.status} />],
          }
        : undefined,
      title: {
        primary:
          settlement.status === RequestStatusEnum.Draft ? (
            <Typography.Text italic>{`${t("draft")}  ${settlement.title}`}</Typography.Text>
          ) : (
            settlement.title
          ),
        tag:
          !(settlement.status === RequestStatusEnum.Draft) && md ? (
            <Space size={"middle"}>
              <Copy text={settlement.code || " "}>
                <Typography.Text type={"secondary"}>{settlement.code}</Typography.Text>
              </Copy>
              <TagStatus status={settlement.status} />
            </Space>
          ) : (
            <TagStatus status={settlement.status === RequestStatusEnum.Draft ? undefined : settlement.status} />
          ),
      },
      key: settlement.code || "",
    })) || [];

  return (
    <>
      <DataHeader
        title={
          <Flex justify={"space-between"}>
            <Typography.Title level={4}>{t("settlement.latelyRequestList")}</Typography.Title>
            <Link
              key={1}
              data-google-track={lastRequestsViewAllId}
              to={{
                pathname: DashboardRoutes.settlement.report.list.url,
                search: qs.stringify({ segment: "request" }),
              }}
            >
              <Button className={"!tw-p-0"} type={"link"}>
                {t("showAll")}
              </Button>
            </Link>
          </Flex>
        }
      />
      <CardList
        grid={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1, column: 0 }}
        dataSource={data}
        loading={loading}
        mode={md ? DisplayModeEnum.List : DisplayModeEnum.Card}
      />
    </>
  );
};

export default LatelyRequestList;
